import React, { useEffect, useMemo, useState } from "react";
import { L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import {
  ArrowRight, Flame, TrendingUp, Network, Sparkles, GitCompareArrows, Loader2,
  BarChart3, Car, Zap, Crown, Award, Layers, ChevronRight,
} from "lucide-react";

// Per-locale URL path slug for the hub itself
const ROUTE_SLUG_BY_LOCALE = {
  de: "vergleich",
  fr: "comparaison",
  it: "confronto",
  en: "compare",
};

function StructuredData({ data, id }) {
  useEffect(() => {
    if (!data) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.compareHub = id;
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
    return () => {
      document
        .querySelectorAll(`script[data-compare-hub="${id}"]`)
        .forEach((n) => n.remove());
    };
  }, [data, id]);
  return null;
}

function setMetaTag(selector, content) {
  if (!content) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const m = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkTag(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="alternate"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function useHubSeoHead({ data, locale }) {
  useEffect(() => {
    if (!data?.ok) return;
    const title = `${data.hero.h1} · CarHive`;
    document.title = title;
    setMetaTag('meta[name="description"]', data.hero.subtitle);
    setMetaTag('meta[property="og:title"]', data.hero.h1);
    setMetaTag('meta[property="og:description"]', data.hero.subtitle);
    setMetaTag('meta[property="og:type"]', "website");
    setMetaTag('meta[property="og:site_name"]', "CarHive");
    setMetaTag('meta[name="twitter:card"]', "summary_large_image");
    setMetaTag('meta[name="twitter:title"]', data.hero.h1);
    setMetaTag('meta[name="twitter:description"]', data.hero.subtitle);

    const origin = window.location.origin;
    Object.entries(ROUTE_SLUG_BY_LOCALE).forEach(([lc, path]) => {
      setLinkTag("alternate", `${origin}/${lc}/${path}`, lc);
    });
    setLinkTag("alternate", `${origin}/de/vergleich`, "x-default");
    const cur = ROUTE_SLUG_BY_LOCALE[locale] || "vergleich";
    setLinkTag("canonical", `${origin}/${locale}/${cur}`);
  }, [data, locale]);
}

function buildItemListJsonLd(data) {
  if (!data?.ok) return null;
  const items = [
    ...(data.trending || []).slice(0, 8),
    ...(data.most_compared_90d || []).slice(0, 8),
  ];
  if (items.length === 0) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": data.hero.h1,
    "description": data.hero.subtitle,
    "numberOfItems": items.length,
    "itemListElement": items.map((it, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": `${it.brand_a} vs ${it.brand_b}`,
      "url": `${origin}/${data.locale}/vergleich/${it.slug}`,
    })),
  };
}

function buildFaqJsonLd(faq) {
  if (!faq || faq.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
}

function buildBreadcrumbJsonLd(locale, h1) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const cur = ROUTE_SLUG_BY_LOCALE[locale] || "vergleich";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "CarHive", "item": `${origin}/${locale}` },
      { "@type": "ListItem", "position": 2, "name": h1, "item": `${origin}/${locale}/${cur}` },
    ],
  };
}

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/80 p-4">
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
        <Icon className="h-3.5 w-3.5 text-red-500" />
        <span>{label}</span>
      </div>
      <p className="mt-1.5 font-display text-2xl font-bold text-white">{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-zinc-500">{hint}</p>}
    </div>
  );
}

function GrowthBadge({ pct, growthLabel, newLabel }) {
  if (pct == null) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-300">
        <Sparkles className="h-3 w-3" /> {newLabel}
      </span>
    );
  }
  const isUp = pct >= 0;
  const color = isUp
    ? "bg-emerald-500/15 text-emerald-300"
    : "bg-zinc-700/40 text-zinc-300";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${color}`}>
      <TrendingUp className={`h-3 w-3 ${isUp ? "" : "rotate-180"}`} />
      {isUp ? "+" : ""}
      {pct}% · {growthLabel}
    </span>
  );
}

function TrendingCard({ pair, labels }) {
  return (
    <Link
      to={`/vergleich/${pair.slug}`}
      className="group relative flex flex-col gap-2 overflow-hidden rounded-xl border border-zinc-800 bg-gradient-to-br from-red-950/30 via-zinc-950 to-zinc-950 p-4 transition hover:border-red-500/40 hover:shadow-[0_0_0_1px_rgba(220,38,38,0.4)]"
      data-testid={`hub-trending-${pair.slug}`}
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-red-500">
          <Flame className="h-3 w-3" /> Hot
        </span>
        <GrowthBadge
          pct={pair.growth_pct}
          growthLabel={labels.growth}
          newLabel={labels.no_growth}
        />
      </div>
      <p className="mt-1 font-display text-lg font-bold leading-tight text-white sm:text-xl">
        {pair.brand_a} <span className="text-red-500">vs</span> {pair.brand_b}
      </p>
      <p className="text-[11px] text-zinc-400">
        {pair.count_7d} {labels.compares} · 7d
      </p>
      <div className="mt-auto flex items-center justify-end pt-1 text-[11px] text-zinc-500 group-hover:text-red-300">
        {labels.compare_cta} <ChevronRight className="ml-0.5 h-3 w-3" />
      </div>
    </Link>
  );
}

function PopularPairCard({ pair, labels }) {
  return (
    <Link
      to={`/vergleich/${pair.slug}`}
      className="group flex items-center justify-between rounded-lg border border-zinc-800 bg-black/40 px-4 py-3 transition hover:border-red-500/40 hover:bg-zinc-950"
      data-testid={`hub-popular-${pair.slug}`}
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-red-600/10 text-red-400">
          <GitCompareArrows className="h-4 w-4" />
        </span>
        <div>
          <p className="font-display text-sm font-semibold text-white">
            {pair.brand_a} <span className="text-zinc-500">vs</span> {pair.brand_b}
          </p>
          {pair.compare_count_90d > 0 && (
            <p className="text-[11px] text-zinc-500">
              {pair.compare_count_90d} {labels.compares} · 90d
            </p>
          )}
        </div>
      </div>
      <ArrowRight className="h-4 w-4 text-zinc-600 group-hover:text-red-400" />
    </Link>
  );
}

function HotModelCard({ model, labels }) {
  return (
    <Link
      to={`/listings?brand=${encodeURIComponent(model.brand)}&model=${encodeURIComponent(model.model)}`}
      className="group flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-950 px-3.5 py-2.5 transition hover:border-red-500/40 hover:text-red-300"
      data-testid={`hub-model-${model.brand}-${model.model}`.replace(/\s+/g, "-")}
    >
      <span className="flex items-center gap-2 text-sm text-zinc-200 group-hover:text-red-300">
        <Car className="h-3.5 w-3.5 text-red-500/70" />
        {model.brand} {model.model}
      </span>
      <span className="text-[11px] text-zinc-500">{model.count} {labels.listings}</span>
    </Link>
  );
}

function BrandChip({ brand, labels }) {
  return (
    <Link
      to={`/listings?brand=${encodeURIComponent(brand.brand)}`}
      className="group inline-flex items-center justify-between gap-3 rounded-full border border-zinc-800 bg-zinc-950 px-4 py-2 text-sm text-zinc-200 transition hover:border-red-500/40 hover:text-red-300"
      data-testid={`hub-brand-${brand.brand}`.replace(/\s+/g, "-")}
    >
      <span className="font-medium">{brand.brand}</span>
      <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-400 group-hover:bg-red-600/20 group-hover:text-red-300">
        {brand.count}
      </span>
    </Link>
  );
}

function SegmentCard({ seg, labels, icon: Icon }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-5" data-testid={`hub-segment-${seg.name}`.replace(/\s+/g, "-")}>
      <div className="flex items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-red-600/15 text-red-400">
          <Icon className="h-4 w-4" />
        </span>
        <p className="font-display text-base font-semibold text-white">{seg.name}</p>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">{seg.desc}</p>
      {seg.pairs && seg.pairs.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {seg.pairs.map((p) => (
            <Link
              key={p.slug}
              to={`/vergleich/${p.slug}`}
              className="rounded-full border border-zinc-800 bg-black/40 px-3 py-1 text-[11px] text-zinc-300 transition hover:border-red-500/40 hover:text-red-300"
              data-testid={`hub-segment-pair-${p.slug}`}
            >
              {p.brand_a} vs {p.brand_b}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const SEGMENT_ICONS = [Zap, Crown, Award, Layers];

export default function CompareHubPage() {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .compareHub(lang)
      .then((r) => {
        if (!r?.ok) {
          setError("not_found");
          setData(null);
        } else {
          setData(r);
        }
      })
      .catch(() => setError("network"))
      .finally(() => setLoading(false));
  }, [lang]);

  useHubSeoHead({ data, locale: lang });
  const itemListLd = useMemo(() => buildItemListJsonLd(data), [data]);
  const faqLd = useMemo(() => buildFaqJsonLd(data?.faq), [data]);
  const crumbLd = useMemo(
    () => (data?.hero?.h1 ? buildBreadcrumbJsonLd(lang, data.hero.h1) : null),
    [data, lang],
  );

  if (loading) {
    return (
      <div
        className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-24 text-sm text-zinc-500 sm:px-6"
        data-testid="hub-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" /> Lade…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6" data-testid="hub-empty">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">Hub</p>
        <h1 className="mt-2 font-display text-3xl font-bold text-white">
          Compare Hub momentan nicht verfügbar
        </h1>
        <Button asChild className="mt-6 bg-red-600 hover:bg-red-500" data-testid="hub-back-btn">
          <Link to="/listings">Alle Fahrzeuge</Link>
        </Button>
      </div>
    );
  }

  const { hero, labels, stats, trending, most_compared_90d, hot_models, top_brands, discovery_pairs, segments, market_trends, ev_paragraphs, luxury_paragraphs, faq } = data;

  return (
    <article className="mx-auto max-w-7xl px-4 pb-20 pt-10 sm:px-6 lg:px-8" data-testid="compare-hub-page">
      <StructuredData data={itemListLd} id="itemlist" />
      <StructuredData data={faqLd} id="faq" />
      <StructuredData data={crumbLd} id="breadcrumb" />

      {/* Hero */}
      <header
        className="relative overflow-hidden rounded-2xl border border-zinc-800 bg-gradient-to-br from-red-950/40 via-zinc-950 to-black px-5 py-12 sm:px-10 sm:py-16"
        data-testid="hub-hero"
      >
        {/* Decorative glow */}
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-red-600/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -left-16 bottom-0 h-56 w-56 rounded-full bg-red-900/30 blur-3xl" aria-hidden="true" />

        <div className="flex items-center gap-2">
          <GitCompareArrows className="h-4 w-4 text-red-500" />
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-red-500">CarHive · Compare Hub</p>
        </div>
        <h1
          className="mt-3 max-w-4xl font-display text-3xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl"
          data-testid="hub-h1"
        >
          {hero.h1}
        </h1>
        <p className="mt-3 max-w-3xl text-sm text-zinc-300 sm:text-base">{hero.subtitle}</p>
        <p className="mt-5 max-w-3xl text-sm leading-relaxed text-zinc-400">{hero.intro}</p>

        <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard icon={Car} label={labels.listings} value={stats.total_listings} />
          <StatCard icon={Award} label="Brands" value={stats.total_brands} />
          <StatCard icon={GitCompareArrows} label="Pair Pages" value={stats.total_pairs} />
          <StatCard icon={BarChart3} label="Trending Pairs" value={trending.length} hint="7d window" />
        </div>
      </header>

      {/* Trending this week */}
      {trending.length > 0 && (
        <section className="mt-14" data-testid="hub-trending-section">
          <div className="flex items-end justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-white sm:text-3xl">
                <Flame className="h-6 w-6 text-red-500" /> {labels.trending_h2}
              </h2>
              <p className="mt-1 text-sm text-zinc-400">{labels.trending_sub}</p>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {trending.map((p) => (
              <TrendingCard key={p.slug} pair={p} labels={labels} />
            ))}
          </div>
        </section>
      )}

      {/* Popular brand compares */}
      {most_compared_90d.length > 0 && (
        <section className="mt-14" data-testid="hub-popular-section">
          <div className="flex items-end justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-white sm:text-3xl">
                <TrendingUp className="h-6 w-6 text-red-500" /> {labels.popular_h2}
              </h2>
              <p className="mt-1 text-sm text-zinc-400">{labels.popular_sub}</p>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {most_compared_90d.map((p) => (
              <PopularPairCard key={p.slug} pair={p} labels={labels} />
            ))}
          </div>
        </section>
      )}

      {/* Hot Models */}
      {hot_models.length > 0 && (
        <section className="mt-14" data-testid="hub-models-section">
          <div className="flex items-end justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-white sm:text-3xl">
                <Car className="h-6 w-6 text-red-500" /> {labels.models_h2}
              </h2>
              <p className="mt-1 text-sm text-zinc-400">{labels.models_sub}</p>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {hot_models.map((m, i) => (
              <HotModelCard key={`${m.brand}-${m.model}-${i}`} model={m} labels={labels} />
            ))}
          </div>
        </section>
      )}

      {/* Top Brands */}
      {top_brands.length > 0 && (
        <section className="mt-14" data-testid="hub-brands-section">
          <div className="flex items-end justify-between gap-3">
            <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-white sm:text-3xl">
              <Award className="h-6 w-6 text-red-500" /> {labels.brands_h2}
            </h2>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {top_brands.map((b) => (
              <BrandChip key={b.brand} brand={b} labels={labels} />
            ))}
          </div>
        </section>
      )}

      {/* Market Trends — long-form SEO content */}
      {market_trends && market_trends.length > 0 && (
        <section
          className="mt-16 rounded-2xl border border-zinc-800 bg-zinc-950 p-6 sm:p-10"
          data-testid="hub-market-trends"
        >
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-red-500" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-500">
              Schweizer Markt 2026
            </p>
          </div>
          <h2 className="mt-2 font-display text-2xl font-bold text-white sm:text-3xl">{labels.market_trends_h2}</h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-zinc-300">
            {market_trends.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </section>
      )}

      {/* Popular Segments */}
      {segments && segments.length > 0 && (
        <section className="mt-14" data-testid="hub-segments-section">
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">{labels.segments_h2}</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {segments.map((seg, i) => (
              <SegmentCard key={seg.name} seg={seg} labels={labels} icon={SEGMENT_ICONS[i % SEGMENT_ICONS.length]} />
            ))}
          </div>
        </section>
      )}

      {/* EV vs ICE */}
      {ev_paragraphs && ev_paragraphs.length > 0 && (
        <section
          className="mt-14 rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-950 via-zinc-950 to-emerald-950/10 p-6 sm:p-10"
          data-testid="hub-ev-section"
        >
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-emerald-400" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-300">EV-Trend</p>
          </div>
          <h2 className="mt-2 font-display text-2xl font-bold text-white sm:text-3xl">{labels.ev_h2}</h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-zinc-300">
            {ev_paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </section>
      )}

      {/* Luxury */}
      {luxury_paragraphs && luxury_paragraphs.length > 0 && (
        <section
          className="mt-14 rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-950 via-zinc-950 to-amber-950/10 p-6 sm:p-10"
          data-testid="hub-luxury-section"
        >
          <div className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-400" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-300">Luxus-Segment</p>
          </div>
          <h2 className="mt-2 font-display text-2xl font-bold text-white sm:text-3xl">{labels.luxury_h2}</h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-zinc-300">
            {luxury_paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </section>
      )}

      {/* Discovery grid — all pairs */}
      {discovery_pairs && discovery_pairs.length > 0 && (
        <section className="mt-14" data-testid="hub-discovery-section">
          <div className="flex items-end justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-white sm:text-3xl">
                <Network className="h-6 w-6 text-red-500" /> {labels.discovery_h2}
              </h2>
              <p className="mt-1 text-sm text-zinc-400">{labels.discovery_sub}</p>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {discovery_pairs.map((p) => (
              <Link
                key={p.slug}
                to={`/vergleich/${p.slug}`}
                className="group flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-950 px-3.5 py-2.5 transition hover:border-red-500/40 hover:bg-black"
                data-testid={`hub-discovery-${p.slug}`}
              >
                <span className="flex items-center gap-2 text-sm text-zinc-200 group-hover:text-red-300">
                  <GitCompareArrows className="h-3.5 w-3.5 text-zinc-500 group-hover:text-red-400" />
                  {p.brand_a} vs {p.brand_b}
                </span>
                {p.compare_count_90d > 0 && (
                  <span className="text-[10px] text-zinc-500">{p.compare_count_90d}×</span>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {faq && faq.length > 0 && (
        <section
          className="mt-14 rounded-2xl border border-zinc-800 bg-zinc-950 p-6 sm:p-10"
          data-testid="hub-faq-section"
        >
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">{labels.faq_h2}</h2>
          <div className="mt-4 divide-y divide-zinc-900">
            {faq.map((f, i) => (
              <details key={i} className="group py-3" data-testid={`hub-faq-item-${i}`}>
                <summary className="cursor-pointer list-none text-base font-semibold text-white">
                  <span className="mr-2 text-red-500">›</span>
                  {f.q}
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-zinc-300">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Footer nav */}
      <footer className="mt-14 flex flex-wrap justify-between gap-3 border-t border-zinc-900 pt-6 text-sm text-zinc-400">
        <Link to="/listings" className="hover:text-red-300">← {labels.view_brand}</Link>
        <Link to="/compare" className="hover:text-red-300" data-testid="hub-footer-compare-tool">
          Compare-Tool öffnen <ArrowRight className="ml-1 inline-block h-3 w-3" />
        </Link>
      </footer>
    </article>
  );
}
